import type { Post } from "../types";

// Helper function to parse HTML entities and escape characters
export const parseHtmlEntities = (text: string): string => {
  if (!text) return '';

  // First decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  let decodedText = textarea.value;

  // Handle common escape sequences
  decodedText = decodedText
    .replace(/\\n/g, '\n')           // New lines
    .replace(/\\t/g, '\t')           // Tabs
    .replace(/\\r/g, '\r')           // Carriage returns
    .replace(/\\"/g, '"')            // Double quotes
    .replace(/\\'/g, "'")            // Single quotes
    .replace(/\\\\/g, '\\')          // Backslashes
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))) // Unicode
    .replace(/\\x([0-9a-fA-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))); // Hex

  return decodedText;
};

// Helper function to format content with proper line breaks
export const formatContent = (content: string, filterEmptyLines: boolean = true): string => {
  if (!content) return '';

  // Parse HTML entities and escape characters
  const parsedContent = parseHtmlEntities(content);

  // Split by newlines and optionally filter out empty lines
  const lines = parsedContent.split('\n');
  const processedLines = filterEmptyLines ? lines.filter(line => line.trim() !== '') : lines;

  // Join lines with proper spacing
  return processedLines.join('\n');
};

/** Normalizes API `subreddit` to display label `r/name`. */
export function formatRedditSubredditDisplay(sub: string | null | undefined): string | null {
  if (!sub?.trim()) return null;
  const s = sub.trim();
  return s.startsWith('r/') ? s : `r/${s}`;
}

// Helper function to get the correct link for a post
export const getPostLink = (reel: Post): string => {
  if (reel.platform === 'INSTAGRAM' && reel.instagram_post?.post_code) {
    return `https://www.instagram.com/p/${reel.instagram_post.post_code}`;
  } else if (reel.platform === 'X' && reel.x_post?.tweet_link) {
    return reel.x_post.tweet_link;
  } else if (reel.platform === 'LINKEDIN' && reel.linkedin_post?.post_url) {
    return reel.linkedin_post.post_url ?? "";
  } else if (reel.platform === 'TIKTOK' && reel.tiktok_post?.post_url) {
    return reel.tiktok_post.post_url;
  } else if (reel.platform === 'REDDIT' && reel.reddit_post?.post_url) {
    return reel.reddit_post.post_url;
  } else if (reel.platform === 'CHROME_BOOKMARK' && reel.chrome_bookmark_post?.url) {
    return reel.chrome_bookmark_post.url;
  }
  return '';
};

// Helper function to get author info
export const getAuthorInfo = (reel: Post) => {
  if (reel.platform === 'INSTAGRAM' && reel.instagram_post) {
    if (reel.instagram_post?.author) {
      return {
        name: reel.instagram_post?.author?.name,
        handle: `@${reel.instagram_post?.author?.username}`,
        username: reel.instagram_post?.author?.username,
        avatar: reel.instagram_post?.author.profile_picture || "/assets/avatar.png",
        profileImage: reel.instagram_post?.author.profile_picture || "/assets/avatar.png",
      };
    } else {
      return {
        name: "Anonymous",
        handle: "@anonymous",
        username: "anonymous",
        avatar: "/assets/avatar.png",
        profileImage: "/assets/avatar.png",
      };
    }
  } else if (reel.platform === 'X' && reel.x_post) {
    // Add defensive checks for undefined author or user
    const author = reel.x_post.author;
    const user = author?.user;

    return {
      name: user?.name,
      handle: `@${user?.username}`,
      username: user?.username,
      avatar: user?.profile_image || "/assets/placeholder.jpg",
      profileImage: user?.profile_image || "/assets/placeholder.jpg",
      category: "Development"
    };
  } else if (reel.platform === 'LINKEDIN' && reel.linkedin_post) {
    const author = reel.linkedin_post.author;
    return {
      name: author?.full_name || "Anonymous",
      handle: `@${author?.full_name?.toLowerCase().replace(/\s+/g, '') || "anonymous"}`,
      username: author?.full_name?.toLowerCase().replace(/\s+/g, '') || "anonymous",
      avatar: author?.profile_image || "/assets/placeholder.jpg",
      profileImage: author?.profile_image || "/assets/placeholder.jpg",
    };
  } else if (reel.platform === 'TIKTOK' && reel.tiktok_post) {
    const author = reel.tiktok_post.author;
    return {
      name: author?.name || "Anonymous",
      handle: `@${author?.username || "anonymous"}`,
      username: author?.username || "anonymous",
      avatar: author?.profile_picture || "/assets/placeholder.jpg",
      profileImage: author?.profile_picture || "/assets/placeholder.jpg",
    };
  } else if (reel.platform === 'REDDIT' && reel.reddit_post) {
    const rp = reel.reddit_post;
    const displaySub = formatRedditSubredditDisplay(rp.subreddit);
    const author = rp.author;
    const u = author?.username || "anonymous";
    return {
      name: displaySub || author?.name || "Anonymous",
      handle: author?.username ? `@${author.username}` : "",
      username: u,
      avatar: author?.profile_picture || "/assets/placeholder.jpg",
      profileImage: author?.profile_picture || "/assets/placeholder.jpg",
    };
  } else if (reel.platform === 'CHROME_BOOKMARK' && reel.chrome_bookmark_post) {
    let hostname = '';
    let siteName = '';
    try {
      hostname = new URL(reel.chrome_bookmark_post.url).hostname.replace(/^www\./, '');
      // "medium.com" → "Medium", "stack-overflow.com" → "Stack Overflow"
      siteName = hostname.split('.')[0].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    } catch (_) {
      hostname = reel.chrome_bookmark_post.url;
      siteName = hostname;
    }
    const faviconUrl = hostname
      ? `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`
      : '';
    return {
      name: siteName,
      handle: '',
      username: hostname,
      avatar: faviconUrl,
      profileImage: faviconUrl,
    };
  }
  // Fallback for missing platform data
  return {
    name: "Anonymous",
    handle: "@anonymous",
    username: "anonymous",
    avatar: "/assets/avatar.png",
    profileImage: "/assets/avatar.png",
  };
};

// Helper function to get post content
export const getPostContent = (reel: Post): string | undefined => {
  if (reel.platform === 'INSTAGRAM' && reel.instagram_post) {
    const content = reel.instagram_post.caption;
    return formatContent(content);
  } else if (reel.platform === 'X' && reel.x_post) {
    const content = reel.x_post.content;
    return formatContent(content);
  } else if (reel.platform === 'LINKEDIN' && reel.linkedin_post) {
    const content = reel.linkedin_post.content;
    return formatContent(content);
  } else if (reel.platform === 'TIKTOK' && reel.tiktok_post) {
    const content = reel.tiktok_post.content;
    return formatContent(content || '');
  } else if (reel.platform === 'REDDIT' && reel.reddit_post) {
    const content = reel.reddit_post.content;
    return formatContent(content || '');
  } else if (reel.platform === 'CHROME_BOOKMARK' && reel.chrome_bookmark_post) {
    return reel.chrome_bookmark_post.title || reel.chrome_bookmark_post.url;
  }
  return undefined;
};

// Function to truncate text with different limits
export const truncateText = (text: string, limit: number): string => {
  if (!text || text.length <= limit) return text;
  return text.substring(0, limit) + '...';
};

// Function to check if content is truncated
export const isContentTruncated = (text: string, limit: number): boolean => {
  return Boolean(text && text.length > limit);
};

// Helper function to get engagement metrics
export const getEngagementMetrics = (reel: Post) => {
  if (reel.platform === 'INSTAGRAM' && reel.instagram_post) {
    // Check if engagement exists and has the required properties
    if (reel.instagram_post.engagement && 
        typeof reel.instagram_post.engagement.likes_count === 'number' &&
        typeof reel.instagram_post.engagement.play_count === 'number') {
      return {
        likes: reel.instagram_post.engagement.likes_count,
        play: reel.instagram_post.engagement.play_count,
      };
    }
    // Fallback for Instagram posts without proper engagement data
    return {
      likes: 0,
      play: 0,
    };
  } else if (reel.platform === 'X' && reel.x_post) {
    // Check if engagement exists before accessing properties
    if (reel.x_post.engagement) {
      return {
        likes: reel.x_post.engagement.favorite_count || 0,
        saves: reel.x_post.engagement.bookmark_count || 0,
        retweets: reel.x_post.engagement.retweet_count || 0,
        comments: reel.x_post.engagement.reply_count || 0
      };
    }
    // Fallback for X posts without engagement data
    return {
      likes: 0,
      saves: 0,
      retweets: 0,
      comments: 0
    };
  } else if (reel.platform === 'LINKEDIN' && reel.linkedin_post) {
    // Check if engagement exists before accessing properties
    if (reel.linkedin_post.engagement) {
      return {
        likes: reel.linkedin_post.engagement.likes || 0,
        shares: reel.linkedin_post.engagement.shares || 0,
        comments: reel.linkedin_post.engagement.comments || 0
      };
    }
    // Fallback for LinkedIn posts without engagement data
    return {
      likes: 0,
      shares: 0,
      comments: 0
    };
  } else if (reel.platform === 'TIKTOK' && reel.tiktok_post) {
    // Check if engagement exists before accessing properties
    if (reel.tiktok_post.engagement) {
      return {
        likes: reel.tiktok_post.engagement.likes_count || 0,
        comments: reel.tiktok_post.engagement.comments_count || 0,
        shares: reel.tiktok_post.engagement.share_count || 0,
        play: reel.tiktok_post.engagement.play_count || 0
      };
    }
    // Fallback for TikTok posts without engagement data
    return {
      likes: 0,
      comments: 0,
      shares: 0,
      play: 0
    };
  } else if (reel.platform === 'REDDIT' && reel.reddit_post) {
    if (reel.reddit_post.engagement) {
      return {
        likes: reel.reddit_post.engagement.likes_count || 0,
        comments: reel.reddit_post.engagement.comments_count || 0,
        shares: reel.reddit_post.engagement.share_count || 0,
        play: reel.reddit_post.engagement.play_count || 0
      };
    }
    return {
      likes: 0,
      comments: 0,
      shares: 0,
      play: 0
    };
  } else if (reel.platform === 'CHROME_BOOKMARK') {
    return null;
  }
  // Fallback for unknown platform or missing data
  return {
    likes: 0,
    saves: 0,
    retweets: 0,
    comments: 0,
    replies: 0
  };
};

// Helper function to format numbers with K, M symbols
export const formatNumber = (num: number | undefined | null): string => {
  if (!num) return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

// Helper function to get post time
export const getPostTime = (reel: Post): string => {
  let postTime;

  // Handle sample data with invalid timestamps
  if (reel.platform === 'INSTAGRAM' && reel.instagram_post) {
    // For sample data, return empty string if timestamp is missing or invalid
    const timestamp = reel.instagram_post.posted_at ?? reel.instagram_post.created_at;
    if (timestamp == null || isNaN(Number(timestamp))) {
      return "";
    }
    postTime = new Date(Number(timestamp) * 1000);
  } else if (reel.platform === 'X' && reel.x_post) {
    postTime = new Date(reel.x_post.posted_at);
  } else if (reel.platform === 'LINKEDIN' && reel.linkedin_post) {
    postTime = new Date(reel.linkedin_post.posted_at);
  } else if (reel.platform === 'TIKTOK' && reel.tiktok_post) {
    // TikTok uses ISO 8601 format (DateTime), same as X
    if (reel.tiktok_post.posted_at) {
      postTime = new Date(reel.tiktok_post.posted_at);
    } else {
      postTime = new Date(reel.created_at);
    }
  } else if (reel.platform === 'REDDIT' && reel.reddit_post) {
    if (reel.reddit_post.posted_at) {
      postTime = new Date(reel.reddit_post.posted_at + 'Z');
    } else {
      postTime = new Date(reel.created_at);
    }
  } else if (reel.platform === 'CHROME_BOOKMARK' && reel.chrome_bookmark_post) {
    if (reel.chrome_bookmark_post.bookmarked_at) {
      postTime = new Date(reel.chrome_bookmark_post.bookmarked_at);
    } else {
      postTime = new Date(reel.created_at);
    }
  } else {
    postTime = new Date(reel.created_at);
  }

  const now = new Date();
  // Ensure both 'now' and 'postTime' are in UTC for correct timing gap
  const nowUTC = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds()
  ));
  const postTimeUTC = new Date(Date.UTC(
    postTime.getUTCFullYear(),
    postTime.getUTCMonth(),
    postTime.getUTCDate(),
    postTime.getUTCHours(),
    postTime.getUTCMinutes(),
    postTime.getUTCSeconds()
  ));
  const diffInHours = Math.floor((nowUTC.getTime() - postTimeUTC.getTime()) / (1000 * 60 * 60));

  if (diffInHours < 1) return "now";
  if (diffInHours < 24) return `${diffInHours}h`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 31) return `${diffInDays}d`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if(diffInMonths < 12){
    return `${diffInMonths}mo`
  }
  else {
    return `${Math.floor(diffInMonths / 12)}y`;
  }
};

// Helper function to check if post has media
export const hasMedia = (reel: Post): boolean => {
  if (reel.platform === 'INSTAGRAM' && reel.instagram_post) {
    return !!reel.instagram_post.thumbnail_url;
  } else if (reel.platform === 'X' && reel.x_post) {
    const hasOldMedia = reel.x_post.media_urls && reel.x_post.media_urls.length > 0;
    return hasOldMedia;
  } else if (reel.platform === 'LINKEDIN' && reel.linkedin_post) {
    return !!(reel.linkedin_post.media_urls && reel.linkedin_post.media_urls.length > 0);
  } else if (reel.platform === 'TIKTOK' && reel.tiktok_post) {
    return !!(reel.tiktok_post.media_urls && reel.tiktok_post.media_urls.length > 0) || !!reel.tiktok_post.thumbnail_url;
  } else if (reel.platform === 'REDDIT' && reel.reddit_post) {
    return !!(reel.reddit_post.media_urls && reel.reddit_post.media_urls.length > 0) || !!reel.reddit_post.thumbnail_url;
  }
  return false;
};

// Helper function to get thumbnail URL
export const getThumbnailUrl = (reel: Post): string | null => {
  if (reel.platform === 'INSTAGRAM' && reel.instagram_post) {
    return reel.instagram_post.thumbnail_url;
  } else if (reel.platform === 'X' && reel.x_post) {
    // For X posts, check if media_urls exists and has content
    if (reel.x_post.media_urls && reel.x_post.media_urls.length > 0) {
      const mediaItem = reel.x_post.media_urls[0];

      // Check if mediaItem is an object with the new structure
      if (typeof mediaItem === 'object' && mediaItem !== null && 'thumbnail_url' in mediaItem) {
        return (mediaItem as any).thumbnail_url;
      }
      // Fallback for string URLs
      else if (typeof mediaItem === 'string') {
        return mediaItem;
      }
    }
  } else if (reel.platform === 'LINKEDIN' && reel.linkedin_post) {
    // For LinkedIn posts, get the first media URL if available
    if (reel.linkedin_post.media_urls && reel.linkedin_post.media_urls.length > 0) {
      return reel.linkedin_post.media_urls[0];
    }
  } else if (reel.platform === 'TIKTOK' && reel.tiktok_post) {
    // For TikTok posts, prefer thumbnail_url, then first media_url thumbnail
    if (reel.tiktok_post.thumbnail_url) {
      return reel.tiktok_post.thumbnail_url;
    } else if (reel.tiktok_post.media_urls && reel.tiktok_post.media_urls.length > 0) {
      const firstMedia = reel.tiktok_post.media_urls[0];
      if (firstMedia && 'thumbnail_url' in firstMedia) {
        return firstMedia.thumbnail_url;
      }
    }
  } else if (reel.platform === 'REDDIT' && reel.reddit_post) {
    if (reel.reddit_post.thumbnail_url) {
      return reel.reddit_post.thumbnail_url;
    } else if (reel.reddit_post.media_urls && reel.reddit_post.media_urls.length > 0) {
      const firstMedia = reel.reddit_post.media_urls[0];
      if (firstMedia && 'thumbnail_url' in firstMedia) {
        return firstMedia.thumbnail_url;
      }
    }
  }
  return null;
};

// Helper function to check if post is a thread
export const isThread = (reel: Post): boolean => {
  return Boolean(reel.platform === 'X' && reel.x_post && reel.x_post.is_thread);
};

// Helper function to handle post click
export const handlePostClick = (reel: Post): void => {
  const link = getPostLink(reel);
  if (link) {
    window.open(link, "_blank", "noopener,noreferrer");
  }
};

// Helper function to generate share text for a post
export const getShareText = (reel: Post): string => {
  const originalUrl = getPostLink(reel);
  if (!originalUrl) {
    throw new Error('Unable to get post URL');
  }
  
  let platformText = 'post';
  if (reel.platform === 'X') {
    platformText = 'tweet';
  } else if (reel.platform === 'LINKEDIN') {
    platformText = 'LinkedIn post';
  } else if (reel.platform === 'TIKTOK') {
    platformText = 'TikTok post';
  } else if (reel.platform === 'REDDIT') {
    platformText = 'Reddit post';
  } else if (reel.platform === 'CHROME_BOOKMARK') {
    platformText = 'bookmark';
  }

  return `Check out this ${platformText} I saved on Instavault!\n\n${originalUrl}\n\nSave and organize your favorite social media content with Instavault: https://instavault.co`;
};
